/**
 * The purchase module API: suppliers, requests, orders and receipts.
 *
 * Every call is organization-scoped — the client attaches X-Tenant-Id
 * automatically from the selected organization.
 */
import { api } from '../lib/apiClient.js';

// --- dashboard -------------------------------------------------------------

export const dashboard = (days = 30) => api.get('/purchase/dashboard', { query: { days } });

// --- suppliers -------------------------------------------------------------

export const listSuppliers = (query) => api.list('/purchase/suppliers', query);

/**
 * Minimal list for a supplier picker.
 *
 * `purpose` is the difference between the two pickers, and it matters. ORDER
 * offers only suppliers that may be committed to. SOURCING also offers
 * prospects, which is what the RFQ vendor picker needs — inviting an
 * unapproved company to quote is how an organization finds out whether to
 * approve them.
 */
export const supplierOptions = (search, purpose = 'ORDER') =>
  api.get('/purchase/suppliers/select', { query: { search, purpose } });

export const getSupplier = (id) => api.get(`/purchase/suppliers/${id}`);
export const createSupplier = (payload) => api.post('/purchase/suppliers', payload);
export const updateSupplier = (id, payload) => api.put(`/purchase/suppliers/${id}`, payload);
/**
 * A reason is MANDATORY for the statuses that stop a supplier being traded
 * with — ON_HOLD, SUSPENDED, REJECTED — and the API returns 422 without one.
 * It is stored on the supplier, not just in the audit log, because the person
 * who needs it is the next buyer looking at the record.
 */
export const setSupplierStatus = (id, status, reason) =>
  api.patch(`/purchase/suppliers/${id}/status`, reason ? { status, reason } : { status });

/** Have we already got this company? Ask before submitting the form. */
export const checkSupplierDuplicates = (query) =>
  api.get('/purchase/suppliers/duplicate-check', { query });

// --- supplier contacts -------------------------------------------------------
//
// A company does not read email. Everything the sourcing flow addresses is a
// PERSON here; the generic address on the supplier row is what is left when
// nobody has said who that person is.

export const supplierContactRoles = () => api.get('/purchase/suppliers/contact-roles');

export const listSupplierContacts = (supplierId) =>
  api.get(`/purchase/suppliers/${supplierId}/contacts`);

export const createSupplierContact = (supplierId, payload) =>
  api.post(`/purchase/suppliers/${supplierId}/contacts`, payload);

export const updateSupplierContact = (supplierId, contactId, payload) =>
  api.put(`/purchase/suppliers/${supplierId}/contacts/${contactId}`, payload);

/**
 * Its own call, not a field on the update: this decides where every future
 * enquiry to the supplier goes.
 */
export const setSupplierContactDefaultRole = (supplierId, contactId, role) =>
  api.post(`/purchase/suppliers/${supplierId}/contacts/${contactId}/default-role`, { role });

export const deleteSupplierContact = (supplierId, contactId) =>
  api.delete(`/purchase/suppliers/${supplierId}/contacts/${contactId}`);
export const deleteSupplier = (id) => api.delete(`/purchase/suppliers/${id}`);

// --- supplier profile ------------------------------------------------------

/**
 * Everything hanging off a supplier in ONE call.
 *
 * The profile screen needs the company, its people, its documents, its
 * addresses, its capabilities, its verification history and what it may
 * currently be used for. Fetching those separately would be eight requests to
 * render one page, so the server assembles them in one round of parallel
 * reads and this asks for the result.
 */
export const supplierProfile = (id) => api.get(`/purchase/suppliers/${id}/profile`);

/** Just the progress bar. The onboarding wizard polls this between steps. */
export const supplierProgress = (id) => api.get(`/purchase/suppliers/${id}/progress`);

// --- supplier verification -------------------------------------------------

/**
 * The checks that apply to THIS supplier, with any answers so far.
 *
 * Not a static list in the frontend: which checks apply depends on the
 * supplier (no tax id, no tax check) and on the organization's configuration,
 * and a hard-coded copy here would drift from the one the server enforces.
 */
export const supplierChecklist = (id) => api.get(`/purchase/suppliers/${id}/verification/checklist`);

export const supplierVerifications = (id) => api.get(`/purchase/suppliers/${id}/verifications`);
export const supplierVerification = (id, verificationId) =>
  api.get(`/purchase/suppliers/${id}/verifications/${verificationId}`);

/** Opens a cycle. `reason` is required when the supplier has been verified before. */
export const startSupplierVerification = (id, payload) =>
  api.post(`/purchase/suppliers/${id}/verifications`, payload);

/**
 * The decision. The ONLY way a supplier becomes verified.
 *
 * `outcome` is VERIFIED, NEEDS_CHANGES or REJECTED, and `reason` is mandatory
 * on all three — including a pass.
 */
export const decideSupplierVerification = (id, payload) =>
  api.post(`/purchase/suppliers/${id}/verification/decision`, payload);

// --- supplier approval -----------------------------------------------------

export const submitSupplierForApproval = (id, note) =>
  api.post(`/purchase/suppliers/${id}/approval/submit`, note ? { note } : {});

/** `decision` is APPROVE or REJECT. A reason is mandatory on a rejection. */
export const decideSupplierApproval = (id, payload) =>
  api.post(`/purchase/suppliers/${id}/approval/decision`, payload);

export const withdrawSupplierApproval = (id, reason) =>
  api.post(`/purchase/suppliers/${id}/approval/withdraw`, { reason });

// --- supplier documents ----------------------------------------------------

export const supplierDocumentTypes = () => api.get('/purchase/suppliers/document-types');
export const listSupplierDocuments = (id) => api.get(`/purchase/suppliers/${id}/documents`);
export const createSupplierDocument = (id, payload) =>
  api.post(`/purchase/suppliers/${id}/documents`, payload);
export const updateSupplierDocument = (id, documentId, payload) =>
  api.put(`/purchase/suppliers/${id}/documents/${documentId}`, payload);

/** Its own call, so accepting a document is its own audit entry. */
export const reviewSupplierDocument = (id, documentId, payload) =>
  api.post(`/purchase/suppliers/${id}/documents/${documentId}/review`, payload);

export const deleteSupplierDocument = (id, documentId) =>
  api.delete(`/purchase/suppliers/${id}/documents/${documentId}`);

/** Organization-wide: what is expiring, and whose. */
export const expiringSupplierDocuments = (days) =>
  api.get('/purchase/suppliers/documents/expiring', { query: days ? { days } : undefined });

// --- supplier banking ------------------------------------------------------

/**
 * Masked for callers without `supplier.bank.manage`.
 *
 * The response carries `meta.masked`, so the screen can say "hidden" rather
 * than rendering empty fields that look like missing data.
 */
export const listSupplierBankAccounts = (id) => api.get(`/purchase/suppliers/${id}/bank-accounts`);
export const createSupplierBankAccount = (id, payload) =>
  api.post(`/purchase/suppliers/${id}/bank-accounts`, payload);
export const updateSupplierBankAccount = (id, accountId, payload) =>
  api.put(`/purchase/suppliers/${id}/bank-accounts/${accountId}`, payload);
export const verifySupplierBankAccount = (id, accountId, note) =>
  api.post(`/purchase/suppliers/${id}/bank-accounts/${accountId}/verify`, note ? { note } : {});
export const setDefaultSupplierBankAccount = (id, accountId) =>
  api.post(`/purchase/suppliers/${id}/bank-accounts/${accountId}/default`);
export const deleteSupplierBankAccount = (id, accountId) =>
  api.delete(`/purchase/suppliers/${id}/bank-accounts/${accountId}`);

// --- supplier addresses and capabilities -----------------------------------

export const listSupplierAddresses = (id) => api.get(`/purchase/suppliers/${id}/addresses`);
export const createSupplierAddress = (id, payload) =>
  api.post(`/purchase/suppliers/${id}/addresses`, payload);
export const updateSupplierAddress = (id, addressId, payload) =>
  api.put(`/purchase/suppliers/${id}/addresses/${addressId}`, payload);
export const deleteSupplierAddress = (id, addressId) =>
  api.delete(`/purchase/suppliers/${id}/addresses/${addressId}`);

export const supplierCapabilityKinds = () => api.get('/purchase/suppliers/capability-kinds');
export const listSupplierCapabilities = (id) => api.get(`/purchase/suppliers/${id}/capabilities`);
export const createSupplierCapability = (id, payload) =>
  api.post(`/purchase/suppliers/${id}/capabilities`, payload);
export const updateSupplierCapability = (id, capabilityId, payload) =>
  api.put(`/purchase/suppliers/${id}/capabilities/${capabilityId}`, payload);
export const deleteSupplierCapability = (id, capabilityId) =>
  api.delete(`/purchase/suppliers/${id}/capabilities/${capabilityId}`);

// --- purchase requests -----------------------------------------------------

export const listRequests = (query) => api.list('/purchase/purchase-requests', query);
export const getRequest = (id) => api.get(`/purchase/purchase-requests/${id}`);
export const createRequest = (payload) => api.post('/purchase/purchase-requests', payload);
export const updateRequest = (id, payload) => api.put(`/purchase/purchase-requests/${id}`, payload);
export const deleteRequest = (id) => api.delete(`/purchase/purchase-requests/${id}`);

export const submitRequest = (id) => api.post(`/purchase/purchase-requests/${id}/submit`);
export const approveRequest = (id, note) => api.post(`/purchase/purchase-requests/${id}/approve`, { note });
/** A reason is mandatory: the requester needs to know what to change. */
export const rejectRequest = (id, note) => api.post(`/purchase/purchase-requests/${id}/reject`, { note });
export const cancelRequest = (id, note) => api.post(`/purchase/purchase-requests/${id}/cancel`, { note });

/** Approved requests with no order yet — the "ready to order" queue. */
export const requestsReadyToOrder = () => api.get('/purchase/purchase-requests/ready-to-order');

// --- purchase orders -------------------------------------------------------

export const listOrders = (query) => api.list('/purchase/purchase-orders', query);
export const getOrder = (id) => api.get(`/purchase/purchase-orders/${id}`);
export const createOrder = (payload) => api.post('/purchase/purchase-orders', payload);
export const updateOrder = (id, payload) => api.put(`/purchase/purchase-orders/${id}`, payload);

export const submitOrder = (id) => api.post(`/purchase/purchase-orders/${id}/submit`);
export const approveOrder = (id, note) => api.post(`/purchase/purchase-orders/${id}/approve`, { note });
export const sendOrder = (id) => api.post(`/purchase/purchase-orders/${id}/send`);
export const returnOrderToDraft = (id, note) =>
  api.post(`/purchase/purchase-orders/${id}/return-to-draft`, { note });
/** A reason is mandatory when cancelling a commitment. */
export const cancelOrder = (id, reason) => api.post(`/purchase/purchase-orders/${id}/cancel`, { reason });

// --- goods receipts --------------------------------------------------------

/** Per line: ordered, received and still outstanding. Pre-fills the form. */
export const outstandingForOrder = (id) => api.get(`/purchase/purchase-orders/${id}/outstanding`);

/**
 * Records a delivery. Send only the lines and quantities that arrived; the
 * order status follows automatically.
 *
 * Over-receipt follows the organization's RECEIVING TOLERANCE: within it the
 * receipt posts as usual; beyond it the answer is still 201, with
 * `approvalRequired: true`, the receipt PENDING_APPROVAL, `overReceipt` per
 * line and the order untouched — or a 400 when approval is switched off.
 */
export const receiveOrder = (id, payload) => api.post(`/purchase/purchase-orders/${id}/receive`, payload);

export const listReceipts = (query) => api.list('/purchase/goods-receipts', query);
export const getReceipt = (id) => api.get(`/purchase/goods-receipts/${id}`);

/**
 * Cancels a posted receipt: inventory takes its stock back out first (and
 * refuses if the goods have been issued since), then the order's received
 * quantities go back. The receipt is kept, marked CANCELLED. A reason of 3 to
 * 500 characters is required. Answers { receipt, purchaseOrder }.
 */
export const cancelReceipt = (id, reason) => api.post(`/purchase/goods-receipts/${id}/cancel`, { reason });

/**
 * Approves an over-tolerance receipt: it then posts like any receipt (order
 * quantities, stock). Refused to whoever recorded it. Answers { receipt, purchaseOrder }.
 */
export const approveReceipt = (id, note) =>
  api.post(`/purchase/goods-receipts/${id}/approve`, note ? { note } : {});

/** Rejects an over-tolerance receipt. Nothing had moved; a reason is required. */
export const rejectReceipt = (id, reason) => api.post(`/purchase/goods-receipts/${id}/reject`, { reason });

// --- purchasing settings ---------------------------------------------------

/** { overReceiptTolerancePercent, allowOverReceiptWithApproval, isDefault, updatedAt, updatedBy } */
export const receivingSettings = () => api.get('/purchase/settings/receiving');
export const updateReceivingSettings = (payload) => api.put('/purchase/settings/receiving', payload);

/** The three-way-match tolerances, with `supplierOverrides`. */
export const matchingSettings = () => api.get('/purchase/settings/matching');
export const updateMatchingSettings = (payload) => api.put('/purchase/settings/matching', payload);
export const setSupplierTolerance = (supplierId, payload) =>
  api.put(`/purchase/settings/matching/suppliers/${supplierId}`, payload);
export const removeSupplierTolerance = (supplierId) => api.delete(`/purchase/settings/matching/suppliers/${supplierId}`);

// --- vendor bills ----------------------------------------------------------
//
// DRAFT -> submit (three-way match) -> MATCHED, or ON_HOLD until an approver
// overrides with a reason -> post (accounting: GRNI cleared, AP item raised).
// Paying happens in Accounts payable; a posted bill shows accounting's answer.

export const listBills = (query, options) => api.list('/purchase/vendor-bills', query, options);
export const getBill = (id) => api.get(`/purchase/vendor-bills/${id}`);

/**
 * With `purchaseOrderId` and no lines, the server pre-fills what has been
 * received and not billed, at the order's prices. A duplicate vendor invoice
 * number (same supplier, same year) answers 409 DUPLICATE_VENDOR_INVOICE;
 * `overrideDuplicate` + `duplicateOverrideReason` (purchase.bill.approve) let it through.
 */
export const createBill = (payload) => api.post('/purchase/vendor-bills', payload);
export const updateBill = (id, payload) => api.put(`/purchase/vendor-bills/${id}`, payload);
export const submitBill = (id) => api.post(`/purchase/vendor-bills/${id}/submit`);
/** Overrides a match hold. A reason is mandatory; the submitter cannot approve. */
export const approveBill = (id, reason) => api.post(`/purchase/vendor-bills/${id}/approve`, { reason });
export const postBill = (id) => api.post(`/purchase/vendor-bills/${id}/post`);
export const cancelBill = (id, reason) => api.post(`/purchase/vendor-bills/${id}/cancel`, { reason });

/** Bills on match hold, each with the lines that hold it. */
export const listMatchExceptions = (query, options) => api.list('/purchase/match-exceptions', query, options);
